import { deployEnvironment, ENVIRONMENT_LABEL } from '@features/clients';
import { Button, Modal, PropagationNotice, WriteError } from '@shared/ui';

import {
    platformDefaultConfirmCopy,
    type PlatformDefaultChange,
} from './platform-default-confirm-copy';

import type { FlagKind } from '../schemas/flag-type';

/**
 * The prod-write confirmation on the platform-default toggles (NOXSCRUM-1529).
 *
 * `pending` is `null` while closed. Setting it opens the dialog *instead of*
 * writing — the toggle's `onChange` no longer calls `mutate` directly, this
 * dialog's confirm button does, in the parent (`FlagDetailPage`), which is
 * where the actual `SetDefaultInput` gets built. Cancelling needs no
 * rollback: the mutation was never started, so the toggle simply renders the
 * value it already had.
 *
 * `kind` puts the flag's type in the copy (NOXSCRUM-1670): a `salesforce`
 * default reaches customers' orgs, a `veevart2` one does not.
 *
 * The copy names the environment this build is deployed in
 * (`VITE_BACKOFFICE_ENV`, via the clients feature's `deployEnvironment`): each
 * environment has its own Backoffice and its own flags table, so that is the
 * whole blast radius of a default written here (NOXSCRUM-1656).
 */
export function PlatformDefaultConfirmDialog({
    flagKey,
    kind,
    pending,
    isPending,
    error,
    onCancel,
    onConfirm,
}: {
    flagKey: string;
    kind: FlagKind;
    pending: PlatformDefaultChange | null;
    isPending: boolean;
    error: Error | null;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const copy = pending
        ? platformDefaultConfirmCopy(flagKey, pending, kind, ENVIRONMENT_LABEL[deployEnvironment()])
        : undefined;

    return (
        <Modal
            open={pending !== null}
            onClose={() => {
                if (!isPending) onCancel();
            }}
            dismissable={!isPending}
            title={copy?.title ?? ''}
            description={copy?.description}
            footer={
                <>
                    <Button variant="ghost" onClick={onCancel} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button disabled={isPending} onClick={onConfirm}>
                        {isPending ? 'Saving…' : 'Confirm'}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4">
                {error && <WriteError error={error} />}
                <PropagationNotice />
            </div>
        </Modal>
    );
}
